/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { speciesNames } from "../utils/speciesNames";
import { generaByFamily } from "../utils/taxonomy";
import TaxonomicImpactPreview from "../components/TaxonomicImpactPreview";

const REVISION_TYPES = [
  {
    id: "scientific_name_change",
    label: "Scientific-name change",
    description: "Replace an existing scientific name with an accepted name.",
  },
  {
    id: "common_name_change",
    label: "Common-name change",
    description: "Update a species' common name without changing its scientific name.",
  },
  {
    id: "genus_transfer",
    label: "Genus transfer",
    description: "Transfer a species to a different genus.",
  },
  {
    id: "species_split",
    label: "Species split",
    description: "Define resulting species and prepare future photo-level reviews.",
  },
  {
    id: "species_merger",
    label: "Species merger",
    description: "Combine records classified under multiple species.",
  },
];

const SCIENTIFIC_NAMES = speciesNames
  .map((entry) => String(entry.scientific || "").trim())
  .filter(Boolean);
const SPECIES_BY_NAME = new Map(
  speciesNames.map((entry) => [
    String(entry.scientific || "").trim().toLowerCase(),
    entry,
  ]),
);
const GENUS_NAMES = [...new Set(Object.values(generaByFamily).flat())]
  .filter(Boolean)
  .sort((a, b) => a.localeCompare(b));
const KNOWN_GENERA = new Set(GENUS_NAMES.map((name) => name.toLowerCase()));
const AVAILABLE_FAMILIES = Object.keys(generaByFamily).sort((a, b) => a.localeCompare(b));

function newGeneraForRevision(record) {
  const proposal = record?.proposal || {};
  const targets = proposal.type === "species_split"
    ? (proposal.resultingSpecies || []).map((species) => species.scientificName)
    : proposal.type === "common_name_change" ? [] : [proposal.acceptedScientificName];
  return [...new Set(targets.map((name) => String(name || "").trim().split(/\s+/)[0]).filter(Boolean))]
    .filter((genus) => !KNOWN_GENERA.has(genus.toLowerCase()));
}


let nextLocalRowId = 0;

function newTaxonRow() {
  nextLocalRowId += 1;
  return {
    id: nextLocalRowId,
    scientificName: "",
    commonName: "",
    distribution: "",
    identificationNotes: "",
  };
}

function newSourceRow() {
  nextLocalRowId += 1;
  return { id: nextLocalRowId, scientificName: "" };
}

function emptyDraft() {
  return {
    sourceScientific: "",
    acceptedScientific: "",
    acceptedCommon: "",
    previousCommon: "",
    previousCommonSource: "",
    splitTargets: [newTaxonRow(), newTaxonRow()],
    mergerSources: [newSourceRow(), newSourceRow()],
    authority: "",
    referenceUrl: "",
    explanation: "",
  };
}

const inputClass =
  "w-full rounded-xl border border-[#93CA87]/50 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition placeholder:text-[#374049]/35 focus:border-[#24C3BE] focus:ring-4 focus:ring-[#24C3BE]/10";

function Field({ label, value, onChange, placeholder, hint, children }) {
  const id = useId();
  return (
    <div className="min-w-0 space-y-1.5">
      <label htmlFor={id} className="block text-xs font-semibold text-[#374049]">
        {label}
      </label>
      {children ? (
        React.Children.map(children, (child) =>
          React.isValidElement(child) &&
          ["input", "select", "textarea"].includes(child.type)
            ? React.cloneElement(child, { id })
            : child,
        )
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className={inputClass}
        />
      )}
      {hint && <p className="text-[11px] leading-5 text-[#374049]/55">{hint}</p>}
    </div>
  );
}

function ScientificNameField({ label, value, onChange, placeholder = "e.g., Bubulcus ibis", hint }) {
  const listId = useId();
  const trimmed = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (trimmed.length < 2) return [];
    const prefix = [];
    const partial = [];
    for (const name of SCIENTIFIC_NAMES) {
      const normalized = name.toLowerCase();
      if (normalized.startsWith(trimmed)) prefix.push(name);
      else if (normalized.includes(trimmed)) partial.push(name);
      if (prefix.length >= 12) break;
    }
    return prefix.concat(partial).slice(0, 12);
  }, [trimmed]);

  const known = SPECIES_BY_NAME.get(trimmed);
  return (
    <Field label={label} hint={hint || (known ? `Catalog common name: ${known.common || "Not listed"}` : "You may enter a name that is not in the suggestion catalog.")}>
      <input
        id={listId}
        type="text"
        autoComplete="off"
        list={`${listId}-suggestions`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={`${inputClass} italic`}
      />
      <datalist id={`${listId}-suggestions`}>
        {matches.map((name) => <option key={name} value={name} />)}
      </datalist>
    </Field>
  );
}

function ProposedNameNote({ name }) {
  if (!name.trim()) return null;
  const entry = SPECIES_BY_NAME.get(name.trim().toLowerCase());
  const genus = name.trim().split(/\s+/)[0];
  const recognizedGenus = KNOWN_GENERA.has(genus.toLowerCase());
  return (
    <p className="mt-2 text-[11px] leading-5 text-[#374049]/60">
      {entry ? "Species already appears in speciesNames.js." : "Species is not currently in speciesNames.js."}{" "}
      {recognizedGenus ? "Genus is present in taxonomy.js." : "Genus is not currently in taxonomy.js; its placement may need updating."}
    </p>
  );
}

function SectionTitle({ number, children, description }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#24C3BE]/15 text-xs font-semibold text-[#01767A]">
          {number}
        </span>
        <h2 className="text-2xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>
          {children}
        </h2>
      </div>
      {description && <p className="ml-11 mt-1 text-xs leading-5 text-[#374049]/60">{description}</p>}
    </div>
  );
}

function validateDraft(type, draft) {
  const errors = [];
  const source = draft.sourceScientific.trim();
  const accepted = draft.acceptedScientific.trim();
  const label = (name) => name.toLowerCase();
  const isSpeciesName = (name) => name.trim().split(/\s+/).length >= 2;

  if (type !== "species_merger" && !source) errors.push("Enter the original scientific name.");
  if (source && !isSpeciesName(source)) errors.push("The original scientific name must include a genus and species epithet.");

  if (["scientific_name_change", "genus_transfer", "species_merger"].includes(type)) {
    if (!accepted) errors.push("Enter the accepted scientific name.");
    else if (!isSpeciesName(accepted)) errors.push("The accepted scientific name must include a genus and species epithet.");
  }

  if (type === "scientific_name_change" && source && accepted && label(source) === label(accepted)) {
    errors.push("The original and accepted scientific names must be different.");
  }

  if (type === "genus_transfer" && source && accepted) {
    const oldGenus = source.split(/\s+/)[0].toLowerCase();
    const newGenus = accepted.split(/\s+/)[0].toLowerCase();
    if (oldGenus === newGenus) errors.push("A genus transfer must change the genus.");
  }

  if (type === "common_name_change") {
    if (!draft.acceptedCommon.trim()) errors.push("Enter the new common name.");
    const current = SPECIES_BY_NAME.get(source.toLowerCase())?.common || "";
    if (current && draft.acceptedCommon.trim().toLowerCase() === current.trim().toLowerCase()) {
      errors.push("The proposed common name matches the current catalog entry.");
    }
  }

  if (type === "species_split") {
    if (draft.splitTargets.length < 2) errors.push("A split requires at least two resulting species.");
    const names = draft.splitTargets.map((row) => row.scientificName.trim());
    if (names.some((name) => !isSpeciesName(name))) errors.push("Every resulting species must have a genus and species name.");
    if (new Set(names.map(label)).size !== names.length) errors.push("Resulting species must have distinct names.");
  }

  if (type === "species_merger") {
    if (draft.mergerSources.length < 2) errors.push("A merger requires at least two original species.");
    const names = draft.mergerSources.map((row) => row.scientificName.trim());
    if (names.some((name) => !isSpeciesName(name))) errors.push("Every original species must have a genus and species name.");
    if (new Set(names.map(label)).size !== names.length) errors.push("Original species in a merger must be distinct.");
  }

  if (draft.referenceUrl.trim()) {
    try {
      const url = new URL(draft.referenceUrl.trim());
      if (!["http:", "https:"].includes(url.protocol)) errors.push("The reference URL must be an HTTP or HTTPS link.");
    } catch {
      errors.push("Enter a valid URL for the taxonomic reference.");
    }
  }

  return errors;
}

function buildProposal(type, draft) {
  const proposal = {
    type,
    originalScientificName: type === "species_merger" ? null : draft.sourceScientific.trim(),
  };

  if (["scientific_name_change", "genus_transfer"].includes(type)) {
    proposal.acceptedScientificName = draft.acceptedScientific.trim();
    proposal.acceptedCommonName = draft.acceptedCommon.trim() || null;
  } else if (type === "common_name_change") {
    proposal.previousCommonName = draft.previousCommonSource.toLowerCase() === draft.sourceScientific.trim().toLowerCase()
      ? draft.previousCommon || null
      : SPECIES_BY_NAME.get(draft.sourceScientific.trim().toLowerCase())?.common || null;
    proposal.acceptedCommonName = draft.acceptedCommon.trim();
  } else if (type === "species_split") {
    proposal.resultingSpecies = draft.splitTargets.map(({ scientificName, commonName, distribution, identificationNotes }) => ({
      scientificName: scientificName.trim(),
      commonName: commonName.trim() || null,
      distribution: distribution.trim() || null,
      identificationNotes: identificationNotes.trim() || null,
    }));
    proposal.requiresUserReview = true;
  } else if (type === "species_merger") {
    proposal.originalSpecies = draft.mergerSources.map((row) => row.scientificName.trim());
    proposal.acceptedScientificName = draft.acceptedScientific.trim();
    proposal.acceptedCommonName = draft.acceptedCommon.trim() || null;
  }

  proposal.reference = {
    authority: draft.authority.trim() || null,
    url: draft.referenceUrl.trim() || null,
  };
  proposal.explanation = draft.explanation.trim() || null;
  return proposal;
}

function draftFromProposal(proposal) {
  const draft = emptyDraft();
  if (!proposal) return draft;

  draft.sourceScientific = proposal.originalScientificName || "";
  draft.acceptedScientific = proposal.acceptedScientificName || "";
  draft.acceptedCommon = proposal.acceptedCommonName || "";
  draft.previousCommon = proposal.previousCommonName || "";
  draft.previousCommonSource = proposal.originalScientificName || "";
  draft.authority = proposal.reference?.authority || "";
  draft.referenceUrl = proposal.reference?.url || "";
  draft.explanation = proposal.explanation || "";

  if (proposal.type === "species_split" && Array.isArray(proposal.resultingSpecies)) {
    draft.splitTargets = proposal.resultingSpecies.map((species) => ({
      ...newTaxonRow(),
      scientificName: species.scientificName || "",
      commonName: species.commonName || "",
      distribution: species.distribution || "",
      identificationNotes: species.identificationNotes || "",
    }));
  }
  if (proposal.type === "species_merger" && Array.isArray(proposal.originalSpecies)) {
    draft.mergerSources = proposal.originalSpecies.map((scientificName) => ({
      ...newSourceRow(),
      scientificName,
    }));
  }
  return draft;
}

function formatRevisionDate(timestamp) {
  const value = timestamp?.toDate?.();
  if (!value) return "Pending timestamp";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(value);
}

function revisionTypeLabel(type) {
  return REVISION_TYPES.find((item) => item.id === type)?.label || type;
}

export default function AdminTaxonomyPage() {
  const [revisionType, setRevisionType] = useState(REVISION_TYPES[0].id);
  const [draft, setDraft] = useState(emptyDraft);
  const [errors, setErrors] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(true);
  const [revisionsError, setRevisionsError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [editingRevisionId, setEditingRevisionId] = useState(null);
  const [editingVersion, setEditingVersion] = useState(null);
  const [historyRevisionId, setHistoryRevisionId] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewReport, setPreviewReport] = useState(null);
  const [previewRevision, setPreviewRevision] = useState(null);
  const [githubRevision, setGithubRevision] = useState(null);
  const [githubPlacements, setGithubPlacements] = useState({});
  const [githubPreview, setGithubPreview] = useState(null);
  const [githubPreviewLoading, setGithubPreviewLoading] = useState(false);
  const [githubCreating, setGithubCreating] = useState(false);
  const [githubRefreshing, setGithubRefreshing] = useState(null);
  const [githubError, setGithubError] = useState("");
  const [githubSuccess, setGithubSuccess] = useState("");
  const [applicationPreview, setApplicationPreview] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(null);
  const [applicationApplying, setApplicationApplying] = useState(null);
  const [applicationError, setApplicationError] = useState("");
  const [applicationMessage, setApplicationMessage] = useState("");
  const [splitNoticePreview, setSplitNoticePreview] = useState(null);
  const [splitNoticeLoading, setSplitNoticeLoading] = useState(null);
  const [splitNoticeError, setSplitNoticeError] = useState("");
  const [recoveryContext, setRecoveryContext] = useState(null);
  const [recoveryReport, setRecoveryReport] = useState(null);
  const [recoveryReason, setRecoveryReason] = useState("");
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [recoveryError, setRecoveryError] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");


  useEffect(() => {
    const revisionsQuery = query(
      collection(db, "taxonomicRevisions"),
      orderBy("updatedAt", "desc"),
      limit(60),
    );
    return onSnapshot(revisionsQuery, (snapshot) => {
      setRevisions(snapshot.docs.map((document) => ({ id: document.id, ...document.data() })));
      setRevisionsLoading(false);
      setRevisionsError("");
    }, (error) => {
      console.error("Failed to load taxonomic revisions:", error);
      setRevisionsLoading(false);
      setRevisionsError("Could not retrieve the revision records. Check administrator access and Firestore rules.");
    });
  }, []);

  useEffect(() => {
    if (!historyRevisionId) {
      setHistoryEntries([]);
      setHistoryLoading(false);
      setHistoryError("");
      return undefined;
    }
    setHistoryLoading(true);
    const historyQuery = query(
      collection(db, "taxonomicRevisions", historyRevisionId, "history"),
      orderBy("createdAt", "desc"),
      limit(60),
    );
    return onSnapshot(historyQuery, (snapshot) => {
      setHistoryEntries(snapshot.docs.map((document) => ({ id: document.id, ...document.data() })));
      setHistoryLoading(false);
      setHistoryError("");
    }, (error) => {
      console.error("Failed to load revision history:", error);
      setHistoryError("Could not load revision history.");
      setHistoryLoading(false);
    });
  }, [historyRevisionId]);

  const previewIsStale = !!previewReport && !!revisions.find((item) =>
    item.id === previewReport.revisionId && item.version !== previewReport.revisionVersion,
  );

  const runImpactPreview = async (record) => {
    if (!record?.id || previewing) return;
    setPreviewReport(null);
    setPreviewRevision({ id: record.id, version: record.version });
    setPreviewError("");
    setPreviewing(true);
    try {
      const previewFunction = httpsCallable(getFunctions(undefined, "southamerica-east1"), "adminPreviewTaxonomicImpact", { timeout: 120000 });
      const response = await previewFunction({ revisionId: record.id, expectedVersion: record.version });
      setPreviewReport(response.data);
    } catch (error) {
      console.error("Failed to preview taxonomic impact:", error);
      setPreviewError(error?.message || "Could not generate the impact preview.");
    } finally {
      setPreviewing(false);
    }
  };

  const previewSplitNotices = async (record) => {
    if (!record?.id || splitNoticeLoading) return;
    setSplitNoticeLoading(record.id);
    setSplitNoticePreview(null);
    setSplitNoticeError("");
    try {
      const callable = httpsCallable(getFunctions(undefined, "southamerica-east1"), "adminPreviewTaxonomicSplitNotices", { timeout: 120000 });
      const response = await callable({ revisionId: record.id, expectedVersion: record.version });
      setSplitNoticePreview(response.data);
    } catch (error) {
      setSplitNoticeError(error?.message || "Could not preview split notifications.");
    } finally {
      setSplitNoticeLoading(null);
    }
  };

  const previewApplication = async (record) => {
    if (!record?.id || applicationLoading) return;
    setApplicationLoading(record.id);
    setApplicationPreview(null);
    setApplicationError("");
    setApplicationMessage("");
    try {
      const callable = httpsCallable(getFunctions(undefined, "southamerica-east1"), "adminPreviewTaxonomicApplication", { timeout: 120000 });
      const response = await callable({ revisionId: record.id, expectedVersion: record.version });
      setApplicationPreview(response.data);
    } catch (error) {
      console.error("Failed to preview taxonomic application:", error);
      setApplicationError(error?.message || "Could not preview the revision application.");
    } finally {
      setApplicationLoading(null);
    }
  };

  const applyRevision = async () => {
    if (!applicationPreview || applicationApplying) return;
    const record = revisions.find((item) => item.id === applicationPreview.revisionId);
    if (!record || record.version !== applicationPreview.revisionVersion ||
      !["github_merged", "application_in_progress"].includes(record.status)) {
      setApplicationError("The revision has changed. Refresh its application preview.");
      return;
    }
    if (!window.confirm(
      "Apply this administrator-approved taxonomy change to user observations? " +
      "You must FIRST have merged the GitHub PR and deployed the updated taxonomy. " +
      "Affected users will receive a taxonomic notification and acknowledge it with OK. Before applying, confirm the GitHub PR is merged and the taxonomy is deployed."
    )) return;
    if (!window.confirm("Final confirmation: modify existing user species documents now? This may take multiple batches and cannot be undone from this interface.")) return;
    setApplicationApplying(record.id);
    setApplicationError("");
    setApplicationMessage("");
    try {
      const callable = httpsCallable(getFunctions(undefined, "southamerica-east1"), "adminApplyTaxonomicRevision", { timeout: 180000 });
      const response = await callable({
        revisionId: record.id,
        expectedVersion: record.version,
        confirmDeployed: true,
        confirmApply: true,
      });
      setApplicationMessage(`${response.data.modifiedUsersThisRun} users and ${response.data.modifiedPhotographsThisRun} photographs processed in this batch. ${response.data.usersRemaining} users remaining.${response.data.failedUsers?.length ? ` ${response.data.failedUsers.length} failed; see server logs before retrying.` : ""}${response.data.consistencyPendingUsers?.length ? ` Cached counts still need reconciliation for ${response.data.consistencyPendingUsers.join(", ")}. Do not reapply photographs; use the reconciliation function.` : ""} Notifications were created for affected users.`);
      setApplicationPreview(null);
    } catch (error) {
      console.error("Failed to apply taxonomic revision:", error);
      setApplicationError(error?.message || "Could not apply the revision.");
    } finally {
      setApplicationApplying(null);
    }
  };

  const recoveryCall = (name, data) => httpsCallable(
    getFunctions(undefined, "southamerica-east1"), name, { timeout: 180000 },
  )(data);

  const openRecovery = async (record) => {
    if (!record?.id || recoveryBusy) return;
    const mode = record.type === "species_split" ? "split" : "application";
    setRecoveryBusy(true); setRecoveryError(""); setRecoveryReport(null); setRecoveryMessage("");
    setRecoveryContext({ revisionId: record.id, mode, records: [], recoveryHold: null });
    try {
      const result = await recoveryCall("adminListTaxonomicRecoveryOwners", { revisionId: record.id, mode });
      setRecoveryContext(result.data);
    } catch (error) { setRecoveryError(error?.message || "Could not load recovery records."); }
    finally { setRecoveryBusy(false); }
  };

  const inspectRecovery = async (ownerUid) => {
    if (!recoveryContext || recoveryBusy) return;
    setRecoveryBusy(true); setRecoveryError(""); setRecoveryReport(null); setRecoveryMessage("");
    try {
      const result = await recoveryCall("adminPreviewTaxonomicRecovery", {
        revisionId: recoveryContext.revisionId, mode: recoveryContext.mode, ownerUid,
      });
      setRecoveryReport(result.data);
    } catch (error) { setRecoveryError(error?.message || "Could not verify the backups."); }
    finally { setRecoveryBusy(false); }
  };

  const reconcileRecovery = async (ownerUid) => {
    if (!recoveryContext || recoveryBusy || !window.confirm(
      "Recalculate this user's cached species/photo statistics from current observations? No photos will move."
    )) return;
    setRecoveryBusy(true); setRecoveryError(""); setRecoveryMessage("");
    try {
      const result = await recoveryCall("adminReconcileTaxonomicOwner", {
        revisionId: recoveryContext.revisionId, mode: recoveryContext.mode, ownerUid, confirmReconcile: true,
      });
      const refreshed = await recoveryCall("adminListTaxonomicRecoveryOwners", {
        revisionId: recoveryContext.revisionId, mode: recoveryContext.mode,
      });
      setRecoveryContext(refreshed.data);
      setRecoveryMessage(`Reconciled ${ownerUid}: ${result.data.speciesCount} species, ${result.data.photoCount} photographs. No observations moved.`);
    } catch (error) { setRecoveryError(error?.message || "Could not reconcile statistics."); }
    finally { setRecoveryBusy(false); }
  };

  const restoreRecovery = async () => {
    if (!recoveryReport?.eligible || !recoveryContext || recoveryBusy) return;
    if (recoveryReason.trim().length < 20) { setRecoveryError("Document the recovery reason (at least 20 characters)."); return; }
    const ownerUid = recoveryReport.ownerUid;
    if (!window.confirm(
      "EMERGENCY RESTORE: This reverts affected species documents to their backed-up state, places the revision on hold, and requires statistics reconciliation. Use ONLY for a confirmed problem. Continue?"
    )) return;
    const phrase = window.prompt(`Type RESTORE ${ownerUid} exactly to confirm:`);
    if (phrase !== `RESTORE ${ownerUid}`) return;
    setRecoveryBusy(true); setRecoveryError(""); setRecoveryMessage("");
    try {
      const result = await recoveryCall("adminRestoreTaxonomicOwner", {
        revisionId: recoveryContext.revisionId, mode: recoveryContext.mode, ownerUid,
        confirmRestore: true, confirmationPhrase: phrase, reason: recoveryReason.trim(),
      });
      const refreshed = await recoveryCall("adminListTaxonomicRecoveryOwners", {
        revisionId: recoveryContext.revisionId, mode: recoveryContext.mode,
      });
      setRecoveryContext(refreshed.data);
      setRecoveryReport(null);
      setRecoveryMessage(`Restored ${result.data.restoredDocuments} documents for ${ownerUid}. Revision is ON HOLD. Reconcile counts and investigate before releasing it.`);
    } catch (error) { setRecoveryError(error?.message || "Recovery was blocked. No observations were changed."); }
    finally { setRecoveryBusy(false); }
  };

  const releaseRecoveryHold = async () => {
    const hold = recoveryContext?.recoveryHold;
    if (!hold || recoveryBusy || recoveryReason.trim().length < 20) {
      setRecoveryError("Document the hold resolution (at least 20 characters) first."); return;
    }
    if (!window.confirm("Release the recovery hold? Confirm the taxonomic inconsistency has been resolved.")) return;
    setRecoveryBusy(true); setRecoveryError("");
    try {
      await recoveryCall("adminReleaseTaxonomicRecoveryHold", {
        revisionId: recoveryContext.revisionId, mode: hold.mode, ownerUid: hold.ownerUid,
        reason: recoveryReason.trim(), confirmRelease: true,
      });
      setRecoveryContext({ ...recoveryContext, recoveryHold: null });
      setRecoveryMessage("Recovery hold released. The resolution was recorded in revision history.");
    } catch (error) { setRecoveryError(error?.message || "Could not release recovery hold."); }
    finally { setRecoveryBusy(false); }
  };

  const openGithubProposal = (record) => {
    if (record.status !== "draft") return;
    setGithubRevision({ id: record.id, version: record.version });
    setGithubPlacements({});
    setGithubPreview(null);
    setGithubError("");
    setGithubSuccess("");
  };

  const previewGithubProposal = async () => {
    if (!githubRevision || githubPreviewLoading) return;
    const record = revisions.find((entry) => entry.id === githubRevision.id);
    if (!record || record.status !== "draft" || record.version !== githubRevision.version) {
      setGithubError("This draft has changed. Reopen GitHub preview from the saved revisions list.");
      return;
    }
    const newGenera = newGeneraForRevision(record);
    const placements = Object.fromEntries(newGenera.map((genus) => [genus, githubPlacements[genus] || ""]));
    if (newGenera.some((genus) => !placements[genus])) {
      setGithubError("Select the family for each genus that does not exist in the published taxonomy.");
      return;
    }
    setGithubPreviewLoading(true);
    setGithubPreview(null);
    setGithubError("");
    setGithubSuccess("");
    try {
      const callable = httpsCallable(getFunctions(undefined, "southamerica-east1"), "adminPreviewTaxonomicGitHub", { timeout: 120000 });
      const response = await callable({ revisionId: record.id, expectedVersion: record.version, genusPlacements: placements });
      setGithubPreview({ ...response.data, genusPlacements: placements });
    } catch (error) {
      console.error("GitHub taxonomy preview failed:", error);
      setGithubError(error?.message || "Could not read the GitHub taxonomy files.");
    } finally {
      setGithubPreviewLoading(false);
    }
  };

  const createGithubPullRequest = async () => {
    if (!githubPreview || !githubRevision || githubCreating) return;
    const record = revisions.find((entry) => entry.id === githubRevision.id);
    if (!record || record.version !== githubPreview.revisionVersion || record.status !== "draft") {
      setGithubError("This revision changed since the preview. Please preview again.");
      return;
    }
    if (!window.confirm(`Create a GitHub pull request for revision ${record.id}? This will create a branch and a PR, but will NOT merge, deploy, or migrate observations.`)) return;
    setGithubCreating(true);
    setGithubError("");
    setGithubSuccess("");
    try {
      const callable = httpsCallable(getFunctions(undefined, "southamerica-east1"), "adminCreateTaxonomicPullRequest", { timeout: 120000 });
      const response = await callable({
        revisionId: record.id,
        expectedVersion: record.version,
        baseSha: githubPreview.baseSha,
        planFingerprint: githubPreview.planFingerprint,
        genusPlacements: githubPreview.genusPlacements,
      });
      setGithubSuccess(`GitHub pull request #${response.data.number} created successfully.`);
      setGithubPreview(null);
    } catch (error) {
      console.error("GitHub PR creation failed:", error);
      setGithubError(error?.message || "Could not create the GitHub pull request.");
    } finally {
      setGithubCreating(false);
    }
  };

  const refreshGithubStatus = async (record) => {
    if (githubRefreshing) return;
    setGithubRefreshing(record.id);
    setGithubError("");
    setGithubSuccess("");
    try {
      const callable = httpsCallable(getFunctions(undefined, "southamerica-east1"), "adminSyncTaxonomicPullRequestStatus", { timeout: 120000 });
      const response = await callable({ revisionId: record.id });
      setGithubSuccess(`GitHub pull request status: ${response.data.githubStatus}.`);
    } catch (error) {
      console.error("GitHub PR status refresh failed:", error);
      setGithubError(error?.message || "Could not refresh the GitHub pull request status.");
    } finally {
      setGithubRefreshing(null);
    }
  };

  const chosenType = REVISION_TYPES.find((item) => item.id === revisionType);
  const update = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setSaveError("");
    setSaveMessage("");
    setErrors([]);
    setProposal(null);
    setCopied(false);
  };
  const changeType = (type) => {
    if (type === revisionType) return;
    setRevisionType(type);
    setEditingRevisionId(null);
    setEditingVersion(null);
    setSaveError("");
    setSaveMessage("");
    setDraft(emptyDraft());
    setErrors([]);
    setProposal(null);
    setCopied(false);
  };
  const changeSplitRow = (rowId, field, value) => {
    update("splitTargets", draft.splitTargets.map((row) =>
      row.id === rowId ? { ...row, [field]: value } : row,
    ));
  };
  const changeMergerSource = (rowId, value) => {
    update("mergerSources", draft.mergerSources.map((row) =>
      row.id === rowId ? { ...row, scientificName: value } : row,
    ));
  };
  const reviewDraft = (event) => {
    event.preventDefault();
    const problems = validateDraft(revisionType, draft);
    setErrors(problems);
    setProposal(problems.length ? null : buildProposal(revisionType, draft));
    setCopied(false);
  };

  const resetDraft = () => {
    setDraft(emptyDraft());
    setErrors([]);
    setProposal(null);
    setCopied(false);
    setEditingRevisionId(null);
    setEditingVersion(null);
    setSaveMessage("");
    setSaveError("");
  };

  const loadSavedDraft = (record) => {
    if (record.status !== "draft") return;
    setRevisionType(record.type);
    setDraft(draftFromProposal(record.proposal));
    setEditingRevisionId(record.id);
    setEditingVersion(record.version);
    setProposal(null);
    setErrors([]);
    setCopied(false);
    setSaveMessage("");
    setSaveError("");
    setHistoryRevisionId(record.id);
  };

  const saveProposal = async () => {
    if (!proposal || saving) return;
    setSaving(true);
    setSaveError("");
    setSaveMessage("");
    setGithubPreview(null);
    try {
      const callable = httpsCallable(getFunctions(undefined, "southamerica-east1"), "adminSaveTaxonomicRevision");
      const payload = { proposal };
      if (editingRevisionId) {
        payload.revisionId = editingRevisionId;
        payload.expectedVersion = editingVersion;
      }
      const response = await callable(payload);
      const result = response.data || {};
      setEditingRevisionId(result.revisionId);
      setEditingVersion(result.version);
      setHistoryRevisionId(result.revisionId);
      setPreviewReport(null);
      setPreviewRevision(null);
      setPreviewError("");
      setProposal(null);
      setSaveMessage(`Revision ${result.revisionId} saved as draft (version ${result.version}). No taxonomy has been published.`);
    } catch (error) {
      console.error("Failed to save taxonomic revision:", error);
      setSaveError(error?.message || "Could not save the revision. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#374049] px-3 py-5 sm:px-6 sm:py-8">
      <div className="mx-auto max-w-7xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FDFBF8] shadow-2xl">
        <header className="border-b border-white/10 bg-[#374049] px-5 py-7 text-center sm:px-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#93CA87]">Restricted Access</p>
          <h1 className="mt-1 text-4xl text-[#FCFFF6] sm:text-5xl" style={{ fontFamily: "Cormorant Garamond" }}>
            Taxonomy Management
          </h1>
          <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-white/65">
            Prepare taxonomic revisions before reviewing affected records and publishing changes.
          </p>
        </header>

        <div className="p-4 sm:p-7">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <Link to="/admin" className="text-sm font-semibold text-[#01767A] hover:text-[#374049]">
              ← Return to Admin Mode
            </Link>
            <span className="rounded-full border border-[#93CA87]/40 bg-white px-3 py-1 text-[11px] font-semibold text-[#374049]/70">
              Step 4 · GitHub proposals
            </span>
          </div>

          <div className="mb-6 rounded-xl border border-[#24C3BE]/30 bg-[#24C3BE]/10 px-4 py-3 text-xs leading-5 text-[#374049]">
            <strong>GitHub pull requests enabled:</strong> Save a revision, inspect its impact, and select <strong>Prepare GitHub PR</strong> to preview changes to the version-controlled taxonomy. A PR does not merge itself, deploy your application, or modify existing observations.
          </div>

          <section className="mb-6 rounded-2xl border border-[#93CA87]/40 bg-white p-4 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>Saved taxonomic revisions</h2>
                <p className="mt-1 text-xs text-[#374049]/60">Up to 60 recently updated records. Drafts can be edited, analyzed, and proposed to GitHub. Pull requests require separate review and merging.</p>
              </div>
              <button type="button" onClick={() => { resetDraft(); setHistoryRevisionId(null); }} className="rounded-xl bg-[#01767A] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#374049]">+ New draft</button>
            </div>
            {revisionsError && <p role="alert" className="mt-4 text-sm text-red-700">{revisionsError}</p>}
            {revisionsLoading ? (
              <p className="mt-4 text-sm text-[#374049]/55">Loading revision records...</p>
            ) : revisions.length === 0 ? (
              <p className="mt-4 rounded-xl border border-dashed border-[#93CA87]/50 bg-[#FCFFF6] px-4 py-5 text-sm text-[#374049]/60">No revisions saved yet. Create a draft below.</p>
            ) : (
              <div className="mt-4 grid max-h-80 gap-2 overflow-y-auto">
                {revisions.map((record) => (
                  <div key={record.id} className={`flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between ${editingRevisionId === record.id ? "border-[#24C3BE] bg-[#24C3BE]/5" : "border-[#93CA87]/35 bg-[#FCFFF6]"}`}>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase text-[#01767A]">{revisionTypeLabel(record.type)}</span>
                        <span className="rounded-full border border-[#93CA87]/50 bg-white px-2 py-0.5 text-[10px] font-semibold text-[#374049]">{record.status || "draft"}</span>
                        <span className="text-[10px] text-[#374049]/45">v{record.version || 1}</span>
                      </div>
                      <p className="mt-1 break-words text-sm font-medium text-[#374049]">{record.title || record.id}</p>
                      <p className="mt-1 text-[10px] text-[#374049]/45">Updated {formatRevisionDate(record.updatedAt)} · GitHub: {record.publication?.githubStatus || "not_started"} · Deployment: {record.publication?.deploymentStatus || "not_started"}</p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      {record.status === "draft" && (
                        <>
                          <button type="button" onClick={() => loadSavedDraft(record)} className="rounded-lg border border-[#01767A]/40 bg-white px-3 py-2 text-xs font-semibold text-[#01767A] hover:bg-[#24C3BE]/10">Edit draft</button>
                          <button type="button" onClick={() => runImpactPreview(record)} disabled={previewing} className="rounded-lg bg-[#01767A] px-3 py-2 text-xs font-semibold text-white hover:bg-[#374049] disabled:cursor-wait disabled:opacity-50">{previewing && previewRevision?.id === record.id ? "Analyzing..." : "Preview impact"}</button>
                          <button type="button" onClick={() => openGithubProposal(record)} className="rounded-lg border border-[#01767A]/40 bg-white px-3 py-2 text-xs font-semibold text-[#01767A] hover:bg-[#24C3BE]/10">Prepare GitHub PR</button>
                        </>
                      )}
                      {["github_merged", "application_in_progress"].includes(record.status) && record.type !== "species_split" && (
                        <button type="button" onClick={() => previewApplication(record)} disabled={!!applicationLoading || !!applicationApplying} className="rounded-lg border border-[#01767A]/40 bg-white px-3 py-2 text-xs font-semibold text-[#01767A] hover:bg-[#24C3BE]/10 disabled:opacity-50">
                          {applicationLoading === record.id ? "Checking..." : record.status === "application_in_progress" ? "Continue application" : "Review application"}
                        </button>
                      )}
                      {record.proposal?.type === "species_split" &&
                        ["github_merged", "split_notifying", "split_notifications_complete"].includes(record.status) && (
                        <button type="button" onClick={() => previewSplitNotices(record)} disabled={!!splitNoticeLoading} className="rounded-lg border border-[#01767A]/40 bg-white px-3 py-2 text-xs font-semibold text-[#01767A] hover:bg-[#24C3BE]/10 disabled:opacity-50">
                          {splitNoticeLoading === record.id ? "Checking..." : "Preview split notifications"}
                        </button>
                      )}
                      {record.publication?.pullRequestUrl && (
                        <a href={record.publication.pullRequestUrl} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-[#01767A]/40 bg-white px-3 py-2 text-xs font-semibold text-[#01767A] hover:bg-[#24C3BE]/10">View GitHub PR ↗</a>
                      )}
                      {record.publication?.pullRequestNumber && (
                        <button type="button" onClick={() => refreshGithubStatus(record)} disabled={githubRefreshing === record.id} className="rounded-lg border border-[#374049]/20 bg-white px-3 py-2 text-xs font-semibold text-[#374049] hover:bg-slate-50 disabled:opacity-50">{githubRefreshing === record.id ? "Checking..." : "Refresh PR status"}</button>
                      )}
                      {record.status !== "draft" && (
                        <button type="button" onClick={() => openRecovery(record)} disabled={recoveryBusy} className="rounded-lg border border-[#374049]/20 bg-white px-3 py-2 text-xs font-semibold text-[#374049] hover:bg-slate-50 disabled:opacity-50">Recovery &amp; audit</button>
                      )}
                      <button type="button" onClick={() => setHistoryRevisionId(historyRevisionId === record.id ? null : record.id)} className="rounded-lg border border-[#374049]/20 bg-white px-3 py-2 text-xs font-semibold text-[#374049] hover:bg-slate-50">{historyRevisionId === record.id ? "Hide history" : "History"}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {splitNoticeError && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">{splitNoticeError}</p>}
            {splitNoticePreview && <div className="mt-4 space-y-2 rounded-xl border border-[#93CA87]/50 bg-[#FCFFF6] p-4 text-xs text-[#374049]">
              <strong className="block text-sm">Species-split notification preview</strong>
              <p>{splitNoticePreview.affectedUsers} users and {splitNoticePreview.affectedPhotographs} photographs affected. {splitNoticePreview.usersStillToNotify} users have not yet received a review notice.</p>
              <p>Read-only. Notifications will not be sent until the Step 8 photograph-review page is deployed and verified.</p>
            </div>}
            {applicationError && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">{applicationError}</p>}
            {applicationMessage && <p role="status" className="mt-4 rounded-xl border border-[#93CA87]/40 bg-[#FCFFF6] px-4 py-3 text-xs text-[#01767A]">{applicationMessage}</p>}
            {applicationPreview && (() => {
              const record = revisions.find((item) => item.id === applicationPreview.revisionId);
              const stillValid = record && record.version === applicationPreview.revisionVersion && ["github_merged", "application_in_progress"].includes(record.status);
              return <div className="mt-5 space-y-3 rounded-2xl border border-[#24C3BE]/40 bg-[#FCFFF6] p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>Apply approved revision</h3>
                  <button type="button" onClick={() => setApplicationPreview(null)} className="text-xs text-[#01767A] hover:underline">Close</button>
                </div>
                <p className="text-xs leading-5 text-[#374049]/70">This is an explicit administrator action, not an automatic migration. Before applying, merge the GitHub PR, pull and deploy the new taxonomy, and verify your backup.</p>
                <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                  <div><strong className="block text-lg text-[#374049]">{applicationPreview.affectedUsersRemaining}</strong> Users remaining</div>
                  <div><strong className="block text-lg text-[#374049]">{applicationPreview.affectedPhotographsRemaining}</strong> Photographs remaining</div>
                  <div><strong className="block text-lg text-[#374049]">{applicationPreview.alreadyProcessedUsers}</strong> Already processed (in current inventory)</div>
                  <div><strong className="block text-lg text-[#374049]">{applicationPreview.failedUsers}</strong> Failed users</div>
                </div>
                <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-900"><strong>Important:</strong> Each affected user now receives one informational notification with an OK acknowledgment. Before any production application, complete the remaining integration and recovery validation (Step 12), verify backups, and deploy the complete workflow together.</p>
                {!stillValid && <p className="text-xs text-red-700">Revision status changed. Refresh before applying.</p>}
                <button type="button" disabled={!stillValid || !!applicationApplying || !!applicationLoading} onClick={applyRevision} className="rounded-lg bg-[#374049] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#01767A] disabled:opacity-40">{applicationApplying ? "Applying this batch..." : "Apply to user records (up to 10 users per batch)"}</button>
              </div>;
            })()}
            {recoveryContext && <div className="mt-5 space-y-4 rounded-2xl border border-[#93CA87]/50 bg-[#FCFFF6] p-4 sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>Taxonomic audit and recovery</h3>
                  <p className="mt-1 break-all text-[11px] text-[#374049]/65">Revision {recoveryContext.revisionId} · {recoveryContext.mode}</p>
                </div>
                <button type="button" onClick={() => { setRecoveryContext(null); setRecoveryReport(null); setRecoveryError(""); }} className="text-xs font-semibold text-[#01767A] hover:underline">Close</button>
              </div>
              <p className="text-xs leading-5 text-[#374049]/75">Inspect administrator-authorized owner operations, failed attempts and stale counts. Backup inspection is read-only. Emergency restoration is blocked if any affected species document has changed since its taxonomic operation.</p>
              {recoveryContext.truncated && <p className="text-xs text-amber-800">Only the first 80 operation records are shown. Additional records require paginated inspection.</p>}
              {recoveryContext.recoveryHold && <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-950"><strong>REVISION ON RECOVERY HOLD.</strong> Owner: {recoveryContext.recoveryHold.ownerUid}. Investigate and reconcile before releasing.</p>}
              {recoveryError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-xs text-red-800">{recoveryError}</p>}
              {recoveryMessage && <p role="status" className="rounded-lg bg-white p-3 text-xs text-[#01767A]">{recoveryMessage}</p>}
              {recoveryBusy && <p className="text-xs text-[#374049]/60">Checking protected recovery records...</p>}
              <div className="max-h-72 space-y-2 overflow-y-auto">
                {(recoveryContext.records || []).map((record) => (
                  <div key={record.ownerUid} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#93CA87]/40 bg-white px-3 py-2 text-xs text-[#374049]">
                    <div className="min-w-0">
                      <p className="break-all font-semibold">{record.ownerUid}</p>
                      <p className="mt-1 text-[#374049]/65">{record.status} · Statistics: {record.consistencyStatus} · Backups: {record.backuppedDocumentCount} · Recovery: {record.recoveryStatus}{record.failureCount ? ` · Failures: ${record.failureCount}` : ""}</p>
                      {record.lastError && <p className="mt-1 break-words text-red-700">{record.lastError}</p>}
                    </div>
                    <div className="flex gap-2">
                      {record.recoveryStatus === "available" && <button type="button" onClick={() => inspectRecovery(record.ownerUid)} disabled={recoveryBusy} className="rounded border border-[#01767A]/50 px-2 py-1 text-[#01767A] disabled:opacity-50">Inspect backup</button>}
                      {(record.status === "applied" || record.status === "review_complete") && <button type="button" onClick={() => reconcileRecovery(record.ownerUid)} disabled={recoveryBusy} className="rounded border border-[#374049]/30 px-2 py-1 disabled:opacity-50">Reconcile counts</button>}
                    </div>
                  </div>
                ))}
                {!recoveryBusy && !recoveryContext.records?.length && <p className="text-xs text-[#374049]/65">No recorded owner operations for this revision.</p>}
              </div>
              {recoveryReport && <div className="space-y-2 rounded-xl border border-[#93CA87]/60 bg-white p-3 text-xs text-[#374049]">
                <strong>Guarded recovery verification · {recoveryReport.ownerUid}</strong>
                <p>{recoveryReport.eligible ? "Every current species document matches its post-application fingerprint and every original backup is verified." : "Restoration is blocked."}</p>
                {(recoveryReport.blockers || []).map((message, i) => <p key={i} className="text-red-700">{message}</p>)}
                {(recoveryReport.documents || []).map((entry) => <p key={entry.speciesId} className="break-all text-[#374049]/70">{entry.speciesId}: backup {entry.backupVerified ? "verified" : "unverified"}; current state {entry.unchangedSinceApplication ? "unchanged" : "changed"}</p>)}
                {recoveryReport.auditEvents?.length > 0 && <div className="mt-3 border-t border-[#93CA87]/40 pt-2">
                  <p className="font-semibold">Owner audit events{recoveryReport.auditTruncated ? " (first 50 shown)" : ""}</p>
                  {(recoveryReport.auditEvents || []).map((event) => <p key={event.id} className="mt-1 break-all text-[#374049]/70">{event.action.replaceAll("_", " ")} · {event.actorUid || "system"}{event.error ? ` · ${event.error}` : ""}{event.speciesCount !== null ? ` · ${event.speciesCount} species / ${event.photoCount} photos` : ""}</p>)}
                </div>}
                {recoveryReport.eligible && <p className="text-amber-900"><strong>Emergency only:</strong> Restoration changes photographs back to their exact prior species documents. It does not revert the GitHub taxonomy PR and will place this revision on hold.</p>}
              </div>}
              <label className="block text-xs font-medium text-[#374049]">Reason for emergency restoration or releasing a hold
                <textarea rows={3} value={recoveryReason} onChange={(event) => setRecoveryReason(event.target.value)} placeholder="Document the verified error, scope and intended recovery action (at least 20 characters)." className="mt-1 w-full rounded-lg border border-[#93CA87]/60 bg-white p-3 text-xs text-[#374049]" />
              </label>
              <div className="flex flex-wrap gap-3">
                {recoveryReport?.eligible && <button type="button" disabled={recoveryBusy || recoveryReason.trim().length < 20} onClick={restoreRecovery} className="rounded-lg border border-red-400 bg-white px-3 py-2 text-xs font-semibold text-red-800 disabled:opacity-40">Emergency restore verified backups</button>}
                {recoveryContext.recoveryHold && <button type="button" disabled={recoveryBusy || recoveryReason.trim().length < 20} onClick={releaseRecoveryHold} className="rounded-lg border border-amber-400 bg-white px-3 py-2 text-xs font-semibold text-amber-900 disabled:opacity-40">Release recovery hold</button>}
              </div>
            </div>}
            {githubError && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">{githubError}</p>}
            {githubSuccess && <p role="status" className="mt-4 rounded-xl border border-[#24C3BE]/40 bg-[#FCFFF6] px-4 py-3 text-xs text-[#01767A]">{githubSuccess}</p>}
            {githubRevision && (
              <div className="mt-5 rounded-xl border border-[#24C3BE]/40 bg-[#FCFFF6] p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>GitHub change proposal</h3>
                    <p className="mt-1 break-all text-[11px] text-[#374049]/60">Saved revision: {githubRevision.id}, version {githubRevision.version}</p>
                  </div>
                  <button type="button" onClick={() => { setGithubRevision(null); setGithubPreview(null); setGithubError(""); }} className="text-xs font-semibold text-[#01767A] hover:underline">Close</button>
                </div>
                {(() => {
                  const record = revisions.find((entry) => entry.id === githubRevision.id);
                  if (!record || record.version !== githubRevision.version || record.status !== "draft") {
                    return <p className="mt-3 text-xs text-red-700">This draft is no longer available for GitHub publication. Choose a current draft.</p>;
                  }
                  const genera = newGeneraForRevision(record);
                  return <>
                    {genera.length > 0 && <div className="mt-4 space-y-3">
                      <p className="text-xs font-semibold text-[#374049]">Assign each new genus to its correct existing family. BioIndex will never guess family placement.</p>
                      {genera.map((genus) => <label key={genus} className="block text-xs text-[#374049]">
                        <span className="block mb-1 italic">{genus}</span>
                        <select value={githubPlacements[genus] || ""} onChange={(event) => {
                          setGithubPlacements((prev) => ({ ...prev, [genus]: event.target.value }));
                          setGithubPreview(null);
                        }} className="w-full rounded-lg border border-[#93CA87]/50 bg-white px-3 py-2 text-sm text-[#374049]">
                          <option value="">Choose an existing family...</option>
                          {AVAILABLE_FAMILIES.map((family) => <option value={family} key={family}>{family}</option>)}
                        </select>
                      </label>)}
                    </div>}
                    <p className="mt-3 text-xs leading-5 text-[#374049]/60">Preview reads the repository without writing. Creating the PR adds a branch and commit, but does not merge, deploy, or migrate photographs.</p>
                    <button type="button" onClick={previewGithubProposal} disabled={githubPreviewLoading || githubCreating} className="mt-4 rounded-lg bg-[#01767A] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#374049] disabled:opacity-50">{githubPreviewLoading ? "Reading GitHub..." : "Preview GitHub changes"}</button>
                    {githubPreview && githubPreview.revisionId === record.id && githubPreview.revisionVersion === record.version && <div className="mt-4 space-y-3 rounded-xl border border-[#93CA87]/50 bg-white p-4">
                      <div className="text-xs font-semibold text-[#374049]">Proposed changes: {githubPreview.repository}, branch {githubPreview.baseBranch}</div>
                      <p className="break-all text-[10px] text-[#374049]/50">Base commit: {githubPreview.baseSha}</p>
                      <div className="text-xs text-[#374049]">Files: {githubPreview.summary.paths.join(", ")}</div>
                      <div className="text-xs text-[#374049]">Species catalog: {githubPreview.summary.speciesNames.added} added, {githubPreview.summary.speciesNames.removed} removed, {githubPreview.summary.speciesNames.updated} updated.</div>
                      <div className="text-xs text-[#374049]">Genus hierarchy: {githubPreview.summary.taxonomy.addedGenera} genera added.</div>
                      <div className="overflow-x-auto rounded-lg border border-[#93CA87]/40">
                        <table className="w-full text-left text-xs text-[#374049]">
                          <thead className="bg-[#FCFFF6] text-[10px] uppercase tracking-wide text-[#374049]/60">
                            <tr><th className="px-3 py-2">Scientific name</th><th className="px-3 py-2">Before</th><th className="px-3 py-2">After</th></tr>
                          </thead>
                          <tbody>{githubPreview.speciesChanges.map((item) => (
                            <tr key={item.scientific} className="border-t border-[#93CA87]/25">
                              <td className="px-3 py-2 italic">{item.scientific}</td>
                              <td className="px-3 py-2">{item.before ? (item.before.common || "No common name") : "Not listed"}</td>
                              <td className="px-3 py-2">{item.after ? (item.after.common || "No common name") : "Removed from suggestions"}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                      <div className="text-xs text-[#374049]">Original species: {githubPreview.names.originalNames.join(", ")}</div>
                      {githubPreview.names.acceptedNames.length > 0 && <div className="text-xs text-[#374049]">Accepted species: {githubPreview.names.acceptedNames.join(", ")}</div>}
                      {githubPreview.summary.taxonomy.placements.map((placement) => <p key={placement} className="text-xs text-[#374049]">New genus placement: {placement}</p>)}
                      {(githubPreview.summary.speciesNames.notices || []).map((notice) => <p key={notice} className="text-xs text-amber-700">{notice}</p>)}
                      <button type="button" onClick={createGithubPullRequest} disabled={githubCreating} className="rounded-lg bg-[#374049] px-4 py-2.5 text-xs font-semibold text-white hover:bg-[#01767A] disabled:opacity-50">{githubCreating ? "Creating GitHub PR..." : "Confirm and create pull request"}</button>
                    </div>}
                  </>;
                })()}
              </div>
            )}
            {previewing && (
              <p role="status" className="mt-4 rounded-xl border border-[#24C3BE]/30 bg-[#FCFFF6] px-4 py-3 text-xs text-[#374049]">Reading the saved taxonomy draft and affected Firestore species records. No data will be modified.</p>
            )}
            {previewError && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">{previewError}</p>}
            {previewReport && !previewIsStale && (
              <TaxonomicImpactPreview
                report={previewReport}
                refreshing={previewing}
                onClose={() => { setPreviewReport(null); setPreviewRevision(null); setPreviewError(""); }}
                onRefresh={() => {
                  const latest = revisions.find((item) => item.id === previewReport.revisionId);
                  if (latest) runImpactPreview(latest);
                }}
              />
            )}
            {previewIsStale && (
              <p role="alert" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">This draft has been updated since its last impact preview. Select <strong>Preview impact</strong> again to analyze the latest saved version.</p>
            )}
            {historyRevisionId && (
              <div className="mt-5 rounded-xl border border-[#93CA87]/40 bg-[#FCFFF6] p-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-[#374049]">Revision history</h3>
                  <button type="button" onClick={() => setHistoryRevisionId(null)} className="text-xs text-[#01767A] hover:underline">Close</button>
                </div>
                <p className="mt-1 break-all text-[10px] text-[#374049]/45">{historyRevisionId}</p>
                {historyError && <p role="alert" className="mt-3 text-xs text-red-700">{historyError}</p>}
                {historyLoading && <p className="mt-3 text-xs text-[#374049]/60">Loading history...</p>}
                <div className="mt-3 space-y-2">
                  {historyEntries.map((entry) => (
                    <details key={entry.id} className="rounded-lg border border-[#93CA87]/30 bg-white px-3 py-2">
                      <summary className="cursor-pointer text-xs font-semibold text-[#374049]">{entry.version ? `v${entry.version} · ` : ""}{entry.action === "draft_created" ? "Draft created" : entry.action === "draft_updated" ? "Draft updated" : (entry.action || "Taxonomic event").replaceAll("_", " ")} · {formatRevisionDate(entry.createdAt)}</summary>
                      <p className="mt-2 text-[10px] text-[#374049]/55">Administrator UID: {entry.actorUid || "system"}{entry.ownerUid ? ` · Affected account: ${entry.ownerUid}` : ""}</p>
                      {entry.reason && <p className="mt-2 text-xs text-[#374049]">Reason: {entry.reason}</p>}
                      {entry.proposalSnapshot && <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-[#FCFFF6] p-3 text-[11px] text-[#374049]">{JSON.stringify(entry.proposalSnapshot, null, 2)}</pre>}
                    </details>
                  ))}
                </div>
              </div>
            )}
          </section>

          <div className="grid items-start gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
            <aside className="rounded-2xl border border-[#93CA87]/40 bg-white p-3 shadow-sm lg:sticky lg:top-5">
              <p className="px-3 pb-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#374049]/50">
                Revision type
              </p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                {REVISION_TYPES.map((type, index) => {
                  const selected = revisionType === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => changeType(type.id)}
                      aria-pressed={selected}
                      className={`rounded-xl border px-3 py-3 text-left transition ${selected
                        ? "border-[#01767A] bg-[#374049] text-white shadow-sm"
                        : "border-[#93CA87]/35 bg-[#FCFFF6] text-[#374049] hover:border-[#24C3BE]/70 hover:bg-[#24C3BE]/10"
                      }`}
                    >
                      <span className={`block text-[10px] font-semibold uppercase tracking-[0.1em] ${selected ? "text-[#93CA87]" : "text-[#01767A]"}`}>
                        Operation {index + 1}
                      </span>
                      <span className="mt-1 block text-sm font-semibold">{type.label}</span>
                      <span className={`mt-1 block text-[11px] leading-4 ${selected ? "text-white/65" : "text-[#374049]/60"}`}>
                        {type.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            <section className="min-w-0 rounded-2xl border border-[#93CA87]/40 bg-white shadow-sm">
              <div className="border-b border-[#93CA87]/30 bg-[#FCFFF6] px-5 py-5 sm:px-7">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#01767A]">Prepare revision</p>
                <h2 className="mt-1 text-3xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>
                  {chosenType.label}
                </h2>
                <p className="mt-1 text-sm text-[#374049]/60">{chosenType.description}</p>
              </div>

              {editingRevisionId && (
                <div className="border-b border-[#93CA87]/35 bg-[#24C3BE]/10 px-5 py-3 text-xs text-[#374049] sm:px-7">
                  Editing saved draft <strong>{editingRevisionId}</strong> (version {editingVersion}). Review and save to create a new history entry.
                </div>
              )}
              {saveMessage && <div role="status" className="border-b border-[#93CA87]/35 bg-[#FCFFF6] px-5 py-3 text-sm text-[#01767A] sm:px-7">{saveMessage}</div>}
              {saveError && <div role="alert" className="border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-700 sm:px-7">{saveError}</div>}
              <form onSubmit={reviewDraft} className="space-y-8 px-5 py-6 sm:px-7">
                <div>
                  <SectionTitle number="1" description="Describe the taxon or taxa involved in this revision.">Taxonomic change</SectionTitle>
                  <div className="space-y-5 pl-0 sm:pl-11">
                    {revisionType !== "species_merger" && (
                      <ScientificNameField
                        label={revisionType === "common_name_change" ? "Scientific name" : "Original scientific name"}
                        value={draft.sourceScientific}
                        onChange={(value) => update("sourceScientific", value)}
                      />
                    )}

                    {revisionType === "common_name_change" && (
                      <>
                        <Field
                          label="Current common name in speciesNames.js"
                          value={SPECIES_BY_NAME.get(draft.sourceScientific.trim().toLowerCase())?.common || "Not listed"}
                        >
                          <div className="rounded-xl border border-[#93CA87]/40 bg-[#FCFFF6] px-4 py-3 text-sm text-[#374049]/75">
                            {SPECIES_BY_NAME.get(draft.sourceScientific.trim().toLowerCase())?.common || "Not listed"}
                          </div>
                        </Field>
                        <Field label="New common name" value={draft.acceptedCommon} onChange={(value) => update("acceptedCommon", value)} placeholder="Enter the revised common name" />
                      </>
                    )}

                    {revisionType === "scientific_name_change" && (
                      <>
                        <ScientificNameField label="Accepted scientific name" value={draft.acceptedScientific} onChange={(value) => update("acceptedScientific", value)} placeholder="e.g., Ardea ibis" />
                        <Field label="Accepted common name (optional)" value={draft.acceptedCommon} onChange={(value) => update("acceptedCommon", value)} placeholder="e.g., Western Cattle Egret" />
                        <ProposedNameNote name={draft.acceptedScientific} />
                      </>
                    )}

                    {revisionType === "genus_transfer" && (
                      <>
                        <ScientificNameField label="Accepted scientific name in the new genus" value={draft.acceptedScientific} onChange={(value) => update("acceptedScientific", value)} placeholder="e.g., Ardea ibis" hint="Enter the complete accepted binomial. The species epithet can change if required by the revision." />
                        <Field label="Accepted common name (optional)" value={draft.acceptedCommon} onChange={(value) => update("acceptedCommon", value)} placeholder="e.g., Western Cattle Egret" />
                        {draft.sourceScientific.trim() && draft.acceptedScientific.trim() && (
                          <div className="rounded-xl border border-[#93CA87]/40 bg-[#FCFFF6] px-4 py-3 text-xs text-[#374049]/75">
                            Genus: <em>{draft.sourceScientific.trim().split(/\s+/)[0]}</em> → <em>{draft.acceptedScientific.trim().split(/\s+/)[0]}</em>
                            <ProposedNameNote name={draft.acceptedScientific} />
                          </div>
                        )}
                      </>
                    )}

                    {revisionType === "species_split" && (
                      <>
                        <p className="text-xs leading-5 text-[#374049]/65">List all resulting species. Geographic guidance and identification notes will support users' future photograph reviews.</p>
                        {draft.splitTargets.map((row, index) => (
                          <div key={row.id} className="rounded-2xl border border-[#93CA87]/45 bg-[#FCFFF6] p-4 sm:p-5">
                            <div className="mb-4 flex items-center justify-between gap-3">
                              <h3 className="text-sm font-semibold text-[#374049]">Resulting species {index + 1}</h3>
                              {draft.splitTargets.length > 2 && (
                                <button type="button" onClick={() => update("splitTargets", draft.splitTargets.filter((item) => item.id !== row.id))} className="text-xs font-semibold text-red-700 hover:underline">
                                  Remove
                                </button>
                              )}
                            </div>
                            <div className="space-y-4">
                              <ScientificNameField label="Scientific name" value={row.scientificName} onChange={(value) => changeSplitRow(row.id, "scientificName", value)} placeholder="e.g., Ardea ibis" />
                              <Field label="Common name (optional)" value={row.commonName} onChange={(value) => changeSplitRow(row.id, "commonName", value)} placeholder="e.g., Western Cattle Egret" />
                              <Field label="Geographic distribution (optional)" value={row.distribution} onChange={(value) => changeSplitRow(row.id, "distribution", value)} placeholder="Describe the typical distribution" />
                              <Field label="Identification guidance (optional)" value={row.identificationNotes} onChange={(value) => changeSplitRow(row.id, "identificationNotes", value)} placeholder="Diagnostic characteristics or relevant cautions" />
                            </div>
                          </div>
                        ))}
                        <button type="button" onClick={() => update("splitTargets", [...draft.splitTargets, newTaxonRow()])} className="rounded-xl border border-[#01767A]/40 bg-white px-4 py-2.5 text-xs font-semibold text-[#01767A] hover:bg-[#24C3BE]/10">
                          + Add resulting species
                        </button>
                      </>
                    )}

                    {revisionType === "species_merger" && (
                      <>
                        <p className="text-xs leading-5 text-[#374049]/65">List the original taxa to combine. The accepted destination may itself be one of the original taxa.</p>
                        {draft.mergerSources.map((row, index) => (
                          <div key={row.id} className="flex items-end gap-2">
                            <div className="min-w-0 flex-1">
                              <ScientificNameField label={`Original species ${index + 1}`} value={row.scientificName} onChange={(value) => changeMergerSource(row.id, value)} />
                            </div>
                            {draft.mergerSources.length > 2 && (
                              <button type="button" onClick={() => update("mergerSources", draft.mergerSources.filter((item) => item.id !== row.id))} className="mb-1 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50">
                                Remove
                              </button>
                            )}
                          </div>
                        ))}
                        <button type="button" onClick={() => update("mergerSources", [...draft.mergerSources, newSourceRow()])} className="rounded-xl border border-[#01767A]/40 bg-white px-4 py-2.5 text-xs font-semibold text-[#01767A] hover:bg-[#24C3BE]/10">
                          + Add original species
                        </button>
                        <div className="border-t border-[#93CA87]/30 pt-5">
                          <ScientificNameField label="Accepted destination species" value={draft.acceptedScientific} onChange={(value) => update("acceptedScientific", value)} placeholder="Scientific name retained after the merger" />
                        </div>
                        <Field label="Accepted common name (optional)" value={draft.acceptedCommon} onChange={(value) => update("acceptedCommon", value)} placeholder="Common name for the resulting taxon" />
                      </>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#93CA87]/30 pt-7">
                  <SectionTitle number="2" description="Supply information supporting the revision. These fields can be completed before publication.">Reference and explanation</SectionTitle>
                  <div className="space-y-5 pl-0 sm:pl-11">
                    <Field label="Taxonomic authority or reference (optional)" value={draft.authority} onChange={(value) => update("authority", value)} placeholder="Publication, authority, or checklist" />
                    <Field label="Reference URL (optional)" value={draft.referenceUrl} onChange={(value) => update("referenceUrl", value)} placeholder="https://..." >
                      <input type="url" value={draft.referenceUrl} onChange={(event) => update("referenceUrl", event.target.value)} className={inputClass} placeholder="https://..." />
                    </Field>
                    <Field label="Explanation or administrator notes (optional)">
                      <textarea rows={4} value={draft.explanation} onChange={(event) => update("explanation", event.target.value)} className={`${inputClass} resize-y`} placeholder="Explain why this revision is appropriate and what users should know." />
                    </Field>
                  </div>
                </div>

                {errors.length > 0 && (
                  <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <p className="font-semibold">Please review the following:</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {errors.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#93CA87]/30 pt-5">
                  <button type="button" onClick={resetDraft} disabled={saving} className="rounded-xl border border-[#374049]/20 px-5 py-3 text-sm font-semibold text-[#374049] hover:bg-[#374049]/5 disabled:opacity-50">
                    {editingRevisionId ? "Cancel editing / clear form" : "Clear form"}
                  </button>
                  <button type="submit" className="rounded-xl bg-[#01767A] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#374049]">
                    Review draft →
                  </button>
                </div>
              </form>

              {proposal && (
                <section className="border-t border-[#93CA87]/35 bg-[#FCFFF6] px-5 py-6 sm:px-7" aria-live="polite">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-2xl text-[#374049]" style={{ fontFamily: "Cormorant Garamond" }}>Draft preview</h3>
                      <p className="mt-1 text-xs text-[#374049]/65">Validated locally. Save to Firestore to preserve this draft. No taxonomy will be published.</p>
                    </div>
                    <button type="button" onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(JSON.stringify(proposal, null, 2));
                        setCopied(true);
                      } catch {
                        setCopied(false);
                      }
                    }} className="rounded-xl border border-[#01767A]/40 bg-white px-4 py-2.5 text-xs font-semibold text-[#01767A] hover:bg-[#24C3BE]/10">
                      {copied ? "Copied" : "Copy draft JSON"}
                    </button>
                  </div>
                  <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-[#93CA87]/35 bg-white p-4 text-xs leading-5 text-[#374049]">{JSON.stringify(proposal, null, 2)}</pre>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs text-[#374049]/60">Save to Firestore, then use Prepare GitHub PR above. GitHub changes are not merged or deployed automatically. Observation migrations remain disabled.</p>
                    <button type="button" onClick={saveProposal} disabled={saving} className="rounded-xl bg-[#01767A] px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#374049] disabled:cursor-wait disabled:opacity-50">
                      {saving ? "Saving draft..." : editingRevisionId ? "Save draft changes" : "Save draft to Firestore"}
                    </button>
                  </div>
                </section>
              )}
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
